import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "@/client/services/user";
import { buildLanguageTracks } from "../utils/languageTracks";
import { DEFAULT_JUDGE0_LANGUAGE, getBoilerplateKey, JUDGE0_LANGUAGES } from "../utils/judge0Languages";
import { mergePlatformSubmissions } from "../utils/platformSubmissions";
import { buildCourseEnrollmentPayload } from "../utils/enrollment";

const API = "/api/integration/external-service/coding-platform";

const unwrap = (res: any) => res?.data?.result ?? res?.data;
const toArray = (v: any): any[] => (Array.isArray(v) ? v : []);

export type CPView =
  | "Dashboard"
  | "Courses"
  | "SkillTests"
  | "Problems"
  | "Submissions"
  | "Bookmarks"
  | "Tracks"
  | "Leaderboard"
  | "Compiler";

/** Maps CPView → URL segment and back */
const VIEW_TO_SLUG: Record<CPView, string> = {
  Dashboard:   "dashboard",
  Courses:     "courses",
  SkillTests:  "skill-tests",
  Problems:    "problems",
  Compiler:    "compiler",
  Submissions: "submissions",
  Bookmarks:   "bookmarks",
  Tracks:      "tracks",
  Leaderboard: "leaderboard",
};

const SLUG_TO_VIEW: Record<string, CPView> = Object.fromEntries(
  Object.entries(VIEW_TO_SLUG).map(([v, s]) => [s, v as CPView]),
);

export const LANGUAGES = JUDGE0_LANGUAGES;

export function useCodingPlatform() {
  const { user } = useUser();
  const studentId = user?.id ?? "";
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();

  // Derive active view from the URL slug; fall back to Dashboard
  const view: CPView = (tab && SLUG_TO_VIEW[tab]) ? SLUG_TO_VIEW[tab] : "Dashboard";

  // Replace the old setView — navigate to the matching slug instead
  const setView = (v: CPView) => {
    navigate(`/coding-platform/${VIEW_TO_SLUG[v]}`, { replace: false });
  };

  const [loading, setLoading] = useState(false);

  // data slices
  const [dashboard, setDashboard] = useState<any>(null);
  const [problems, setProblems] = useState<any[]>([]);
  const [facets, setFacets] = useState<any>({});
  const [problemTotal, setProblemTotal] = useState(0);
  const [problemPage, setProblemPage] = useState(0);
  // Seed the limit based on which tab we're on at load time
  const [problemLimit, setProblemLimit] = useState(() => view === "Problems" ? 10 : 5);
  const [selectedProblem, setSelectedProblem] = useState<any>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [skillTestSubmissions, setSkillTestSubmissions] = useState<any[]>([]);
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [courseBookmarks, setCourseBookmarks] = useState<any[]>([]);
  const [recentlyViewedCourses, setRecentlyViewedCourses] = useState<any[]>([]);
  const [tracks, setTracks] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [contests, setContests] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);

  // filter state
  const [filters, setFilters] = useState({ search: "", difficulty: "", topic: "", company: "", tag: "", language: "", status: "" });

  // editor state
  const [language, setLanguage] = useState(DEFAULT_JUDGE0_LANGUAGE);
  const [code, setCode] = useState(DEFAULT_JUDGE0_LANGUAGE.boilerplate);
  const [customInput, setCustomInput] = useState("");
  const [execution, setExecution] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState<{ type: "success" | "error" | "info" | "warning"; message: string } | null>(null);

  // ── fetch helpers ──────────────────────────────────────────────────────────

  const fetchDashboard = useCallback(async () => {
    if (!studentId) return;
    const res = await axios.get(`${API}/dashboard/${studentId}`);
    const data = unwrap(res);
    setDashboard(data);
    setProfile(data?.profile ?? null);
    setNotifications(toArray(data?.notifications));
  }, [studentId]);

  const fetchProblems = useCallback(async (page = 0, f = filters, limit = problemLimit) => {
    if (!studentId) return;
    const params: any = { studentId, page: page + 1, limit,
      search: f.search || undefined, difficulty: f.difficulty || undefined,
      topics: f.topic || undefined, companies: f.company || undefined,
      tags: f.tag || undefined, language: f.language || undefined,
      status: f.status || undefined };
    const res = await axios.get(`${API}/problems`, { params });
    const data = unwrap(res);
    setProblems(toArray(data?.data ?? data));
    setProblemTotal(typeof data?.total === "number" ? data.total : 0);
    setFacets(data?.facets ?? {});
  }, [studentId, filters, problemLimit]);

  const fetchSecondary = useCallback(async () => {
    if (!studentId) return;
    const [sub, skillSub, coursesRes, skillTestsRes, bm, cbm, rvc, co, lb, ct, pr, nt] = await Promise.all([
      axios.get(`${API}/submissions/${studentId}`, { params: { page: 1, limit: 50 } }),
      axios.get(`${API}/skill-tests/history/${studentId}`),
      axios.get(`${API}/courses`, { params: { studentId, page: 1, limit: 100 } }),
      axios.get(`${API}/skill-tests`, { params: { studentId, page: 1, limit: 100 } }),
      axios.get(`${API}/bookmarks/${studentId}`, { params: { page: 1, limit: 50 } }),
      axios.get(`${API}/courses/bookmarks/${studentId}`),
      axios.get(`${API}/courses/recently-viewed/${studentId}`),
      axios.get(`${API}/companies/${studentId}`),
      axios.get(`${API}/leaderboard`, { params: { page: 1, limit: 25 } }),
      axios.get(`${API}/contests`),
      axios.get(`${API}/profile/${studentId}`),
      axios.get(`${API}/notifications/${studentId}`, { params: { page: 1, limit: 20 } }),
    ]);
    setSubmissions(toArray(unwrap(sub)?.data ?? unwrap(sub)));
    setSkillTestSubmissions(toArray(unwrap(skillSub)));
    const courses = toArray(unwrap(coursesRes)?.data ?? unwrap(coursesRes));
    const skillTests = toArray(unwrap(skillTestsRes)?.data ?? unwrap(skillTestsRes));
    setTracks(buildLanguageTracks(courses, skillTests));
    setBookmarks(toArray(unwrap(bm)?.data ?? unwrap(bm)));
    setCourseBookmarks(toArray(unwrap(cbm)));
    setRecentlyViewedCourses(toArray(unwrap(rvc)));
    setCompanies(toArray(unwrap(co)));
    setLeaderboard(toArray(unwrap(lb)?.data ?? unwrap(lb)));
    setContests(toArray(unwrap(ct)));
    setProfile(unwrap(pr));
    setNotifications(toArray(unwrap(nt)?.data ?? unwrap(nt)));
  }, [studentId]);

  // ── initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    Promise.all([fetchDashboard(), fetchProblems(0), fetchSecondary()])
      .catch(() => setNotice({ type: "error", message: "Failed to load platform data." }))
      .finally(() => setLoading(false));
  }, [studentId]);

  // ── actions ────────────────────────────────────────────────────────────────

  const loadProblem = async (problemId: string) => {
    const res = await axios.get(`${API}/problems/${problemId}`, { params: { studentId } });
    const data = unwrap(res);
    setSelectedProblem(data);
    setCode(data?.boilerplateCode?.[getBoilerplateKey(language)] ?? language.boilerplate);
    setExecution(null);
    // Navigation is handled by the caller — no setView here
  };

  const toggleBookmark = async (problemId: string) => {
    await axios.post(`${API}/bookmarks/toggle`, { studentId, problemId });
    await Promise.all([fetchProblems(problemPage), fetchSecondary()]);
  };

  const toggleCourseBookmark = async (courseId: string) => {
    await axios.post(`${API}/courses/bookmark/toggle`, { studentId, courseId });
    await refreshCourseBookmarks();
  };

  const enrollCourse = async (courseId: string) => {
    await axios.post(`${API}/courses/enroll`, buildCourseEnrollmentPayload(user, courseId));
    await refreshCourseBookmarks();
  };

  const refreshCourseBookmarks = useCallback(async () => {
    if (!studentId) return;
    const res = await axios.get(`${API}/courses/bookmarks/${studentId}`);
    setCourseBookmarks(toArray(unwrap(res)));
  }, [studentId]);

  const execute = async (submit: boolean) => {
    const problem = selectedProblem ?? problems[0];
    if (!problem) return;
    setRunning(true);
    setExecution(null);
    try {
      const res = await axios.post(`${API}/${submit ? "submit" : "run"}`, {
        studentId,
        problemId: problem.id ?? problem._id,
        code, programmingLanguage: language.key ?? language.name, programmingLanguageId: language.id,
        customInput: submit ? undefined : customInput || undefined,
      });
      const data = unwrap(res);
      setExecution(data);
      // ── Snackbar message for Run / Submit ──
      const status = String(data?.status ?? "");
      const passed = Number(data?.passedTestCases ?? 0);
      const total = Number(data?.totalTestCases ?? 0);
      if (submit) {
        if (status === "accepted") {
          setNotice({ type: "success", message: "Submitted successfully" });
        } else {
          const label = status.replace(/_/g, " ");
          setNotice({
            type: "error",
            message: label ? `Submitted — ${label.charAt(0).toUpperCase()}${label.slice(1)}` : "Submission failed",
          });
        }
      } else {
        if (status === "accepted") {
          setNotice({ type: "success", message: "All test cases passed" });
        } else if (total > 0) {
          const failed = total - passed;
          const failWord = failed === 1 ? "test case failed" : "test cases failed";
          const msg = failed === total
            ? `All ${total} test cases failed`
            : `${failed} ${failWord} (${passed}/${total} passed)`;
          setNotice({ type: failed === total ? "error" : "warning", message: msg });
        } else {
          setNotice({ type: "info", message: "Code execution finished." });
        }
      }
      if (submit) await Promise.all([fetchDashboard(), fetchProblems(problemPage), fetchSecondary()]);
    } catch (e: any) {
      setNotice({ type: "error", message: e?.response?.data?.message ?? "Code execution failed." });
    } finally {
      setRunning(false);
    }
  };

  const applyFilters = (next: typeof filters) => {
    setFilters(next);
    setProblemPage(0);
    fetchProblems(0, next).catch(() => {});
  };

  const changeProblemPage = (p: number) => {
    setProblemPage(p);
    fetchProblems(p).catch(() => {});
  };

  const setProblemPageSize = (limit: number) => {
    setProblemLimit(limit);
    setProblemPage(0);
    fetchProblems(0, filters, limit).catch(() => {});
  };

  const platformSubmissions = useMemo(
    () => mergePlatformSubmissions(submissions, skillTestSubmissions),
    [submissions, skillTestSubmissions],
  );

  return {
    studentId, view, setView,
    loading, notice, setNotice,
    dashboard, problems, facets, problemTotal, problemPage, problemLimit,
    selectedProblem, setSelectedProblem,
    submissions, skillTestSubmissions, platformSubmissions,
    bookmarks, courseBookmarks, recentlyViewedCourses,
    tracks, companies, leaderboard, contests, profile, notifications,
    filters, applyFilters, changeProblemPage, setProblemPageSize,
    language, setLanguage, code, setCode,
    customInput, setCustomInput,
    execution, running,
    loadProblem, toggleBookmark, toggleCourseBookmark, enrollCourse, refreshCourseBookmarks, execute,
    fetchSecondary,
  };
}
